// src/utils/Nom035SecIReportGenerator.js

import jsPDF from "jspdf";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import questionnaireScopeApi from "../api/questionnaireScopeApi";
import nom35SecIAnalyticsApi from "../api/nom35SecIAnalyticsApi";
import calculateNom035MinimumRespondents from "./nom035SampleSize";

Chart.register(ChartDataLabels);
Chart.defaults.set("plugins.datalabels", {
  anchor: "center",
  align: "center",
  color: "black",
  font: { size: 12 },
  formatter: (value) => value,
});

const currentYear = new Date().getFullYear();

function loadLogo(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({
        dataURL: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = url;
  });
}

async function renderChart({ type, data, options = {} }) {
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 300;
  const ctx = canvas.getContext("2d");

  new Chart(ctx, {
    type,
    data,
    plugins: [ChartDataLabels],
    options: {
      responsive: false,
      animation: false,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      ...options,
    },
  });

  await new Promise((r) => requestAnimationFrame(r));
  return canvas;
}

export async function generateNom035SecIReport(analyticsScopeId, filters = {}, metadataScopeId = analyticsScopeId) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const { width: pageW, height: pageH } = pdf.internal.pageSize;
  const margin = 40;
  const headerH = 60;
  const contentW = pageW - margin * 2;
  const lineH = 14;

  const company = await questionnaireScopeApi.getById(metadataScopeId);
  const logoUrl = company.logoUrl || "/logo512.png";

  const { totalResponses } =
    await nom35SecIAnalyticsApi.getResponsesCountByCompanyId(analyticsScopeId, filters);

  const applicationId = filters.applicationId;
  const { totalEmployees = 0 } = await nom35SecIAnalyticsApi.getRespondantsByCompanyId(metadataScopeId, applicationId);
  const N = totalEmployees;
  const minRespondents = calculateNom035MinimumRespondents(N);

  let logo;
  try {
    logo = await loadLogo(logoUrl);
  } catch (e) {
    console.error("Error cargando logo:", e);
  }

  function drawHeader() {
    pdf.setFillColor("#282c34");
    pdf.rect(0, 0, pageW, headerH, "F");
    if (logo) {
      const maxH = headerH - 10;
      const ratio = logo.width / logo.height;
      const drawH = maxH;
      const drawW = ratio * drawH;
      const x = (pageW - drawW) / 2;
      const y = (headerH - drawH) / 2;
      pdf.addImage(logo.dataURL, "PNG", x, y, drawW, drawH);
    }
  }

  function addCover() {
    pdf.setFont("helvetica", "bold").setFontSize(36);
    pdf.text("Deli Life Wellness", pageW / 2, 100, { align: "center" });

    if (logo) {
      const maxW = pageW * 0.6;
      const ratio = logo.height / logo.width;
      const drawW = maxW;
      const drawH = maxW * ratio;
      const x = (pageW - drawW) / 2;
      const y = 120;
      pdf.addImage(logo.dataURL, "PNG", x, y, drawW, drawH);
    }

    pdf.setFont("helvetica", "bold").setFontSize(24);
    const logoH = logo ? pageW * 0.6 * (logo.height / logo.width) : 0;
    pdf.text("Aplicación Cuestionario NOM-035 Guía I", pageW / 2, 120 + logoH + 40, {
      align: "center",
    });

    const desc =
      "Identificación de los Trabajadores que Fueron Sujetos a\n" +
      "Acontecimientos Traumáticos Severos";

    pdf.setFont("helvetica", "normal").setFontSize(12);
    const descLines = pdf.splitTextToSize(desc, contentW);
    pdf.text(descLines, margin, 120 + logoH + 100);

    let y = 120 + logoH + 100 + descLines.length * lineH + 20;
    pdf.setFont("helvetica", "bold").setFontSize(14);
    pdf.text(`Empresa: ${company.name}`, margin, y);
    y += lineH;
    pdf.text(`${filters.applicationId ? "Total de empleados" : "Registros en aplicaciones"}: ${N}`, margin, y);
    y += lineH;
    pdf.text(`${filters.applicationId ? "Total de encuestados" : "Respuestas acumuladas"}: ${totalResponses}`, margin, y);
    y += lineH;
    pdf.text(filters.applicationId ? `Mínimo de encuestados requeridos: ${minRespondents}` : "Consolidado de todas las aplicaciones; puede incluir personas repetidas.", margin, y);
    y += lineH;
    pdf.text(`Fecha de aplicación: ${currentYear}`, margin, y);
  }

  async function addClinicalPage() {
    pdf.addPage();
    drawHeader();

    pdf.setFont("helvetica", "bold").setFontSize(18);
    pdf.text(
      "Cuestionario 1 - Acontecimientos Traumáticos Severos",
      margin,
      headerH + 30
    );

    const res = await nom35SecIAnalyticsApi.getClinicalSummaryByCompanyId(
      analyticsScopeId,
      filters
    );
    const t = res.counts?.traumaticEvent || 0;
    const r = res.counts?.requireClinicalAttention || 0;
    const n = res.counts?.noRequireClinicalAttention || 0;
    const max = Math.max(t, r, n, 1);

    const canvas = await renderChart({
      type: "bar",
      data: {
        labels: [
          "Eventos traumáticos",
          "Requiere atención médica",
          "No requiere atención médica",
        ],
        datasets: [{ data: [t, r, n], backgroundColor: ["#FFA500", "red", "#ffe951"] }],
      },
      options: {
        scales: {
          y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } },
        },
      },
    });

    const yImg = headerH + 70;
    const imgData = canvas.toDataURL("image/png");
    const imgW = contentW;
    const imgH = (canvas.height * imgW) / canvas.width;
    pdf.addImage(imgData, "PNG", margin, yImg, imgW, imgH);

    let y = yImg + imgH + 20;
    pdf.setFont("helvetica", "normal").setFontSize(12);
    pdf.text(`Eventos traumáticos: ${t}`, margin, y);
    y += lineH;
    pdf.text(`Requiere atención médica: ${r}`, margin, y);
    y += lineH;
    pdf.text(`No requiere atención médica: ${n}`, margin, y);
    y += lineH;

    for (const q of res.questions || []) {
      const value =
        typeof q.average === "number" ? q.average.toFixed(2) : String(q.average ?? "");
      const txt = `${q.question}: ${value}`;
      const lines = pdf.splitTextToSize(txt, contentW);
      for (const line of lines) {
        if (y + lineH > pageH - margin) {
          // En teoría no debería pasar para Guía I, pero prevenimos desborde.
          pdf.addPage();
          drawHeader();
          y = headerH + 20;
        }
        pdf.text(line, margin, y);
        y += lineH;
      }
    }
  }

  addCover();
  await addClinicalPage();

  pdf.save(`Reporte_NOM035_GuiaI_${company.name}.pdf`);
}
